import { useMemo } from "react";
import { EventBadgeStamp } from "./EventBadgeStamp";
import type { PublicEvent } from "../types/public";
import { formatDoorCurtainSlots, getPublicEventBadge, publicPerformersList } from "../types/public";
import { sortByDateTimeThenVenue } from "../lib/event-sort";

/** 同一 event_name（前後空白除く）の公演を横並び用にまとめる */
function groupOthersByNormalizedTitle(events: PublicEvent[]): PublicEvent[][] {
  const order: string[] = [];
  const byKey = new Map<string, PublicEvent[]>();
  for (const e of events) {
    const raw = String(e.event_name ?? "").trim();
    const key = raw.length ? raw : `__empty__:${e.id}`;
    if (!byKey.has(key)) {
      byKey.set(key, []);
      order.push(key);
    }
    byKey.get(key)!.push(e);
  }
  return order.map((k) => byKey.get(k)!);
}

function OtherDetail({
  e,
  onCorrectionRequest,
  canDeleteEvent,
  onDeleteRequest,
}: {
  e: PublicEvent;
  onCorrectionRequest?: (e: PublicEvent) => void;
  canDeleteEvent?: (e: PublicEvent) => boolean;
  onDeleteRequest?: (e: PublicEvent) => void;
}) {
  const badge = getPublicEventBadge(e);
  const doorSlots = formatDoorCurtainSlots(e);
  const perfLine = publicPerformersList(e);
  return (
    <article className="event event-other-row">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <h3 className="title" style={{ fontSize: 14, flex: 1, minWidth: 0 }}>
          {e.event_name}
        </h3>
        <EventBadgeStamp badge={badge} size="md" />
      </div>
      {e.fan_group_name || e.fan_artist_name || e.performance_count != null || e.source_title ? (
        <div className="meta" style={{ lineHeight: 1.55 }}>
          {e.fan_group_name ? <div>グループ名：{e.fan_group_name}</div> : null}
          {e.fan_artist_name ? <div>アーティスト名：{e.fan_artist_name}</div> : null}
          <div>開催日：{e.event_date}</div>
          <div>開催都道府県：{e.prefecture ?? "—"}</div>
          {e.performance_count != null ? <div>公演数：{e.performance_count}</div> : null}
          {e.source_title ? <div>取得タイトル：{e.source_title}</div> : null}
        </div>
      ) : null}
      <div className="meta">出演：{perfLine.length ? perfLine.join(" / ") : "—"}</div>
      {doorSlots ? (
        <div className="meta">{`${e.venue ?? "会場未定"} / ${doorSlots}`}</div>
      ) : e.performance_text?.trim() ? (
        <div className="meta" style={{ whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.45 }}>
          {`${e.venue ?? "会場未定"}\n${e.performance_text!.trim()}`}
        </div>
      ) : (
        <div className="meta">{`${e.venue ?? "会場未定"} / 開演 ${e.curtain_time ?? e.time_start ?? "—"}`}</div>
      )}
      {e.performance_text?.trim() && doorSlots ? (
        <div className="meta" style={{ whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.45 }}>
          {e.performance_text}
        </div>
      ) : null}
      <div className="row day-other-actions" style={{ marginTop: 10, gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {canDeleteEvent?.(e) && onDeleteRequest ? (
          <button type="button" className="btn small secondary" onClick={() => onDeleteRequest(e)}>
            自分の投稿を削除
          </button>
        ) : null}
        {onCorrectionRequest ? (
          <button type="button" className="btn small" onClick={() => onCorrectionRequest(e)}>
            間違ってるよ！
          </button>
        ) : null}
        {e.detail_url ? (
          <a className="btn small secondary" href={e.detail_url} target="_blank" rel="noreferrer">
            🔗
          </a>
        ) : null}
      </div>
    </article>
  );
}

export function PublicDayOthersSection({
  events,
  onCorrectionRequest,
  canDeleteEvent,
  onDeleteRequest,
  title = "他グループ（開演が早い順・かぶり確認用）",
  embedded = false,
}: {
  events: PublicEvent[];
  onCorrectionRequest?: (e: PublicEvent) => void;
  canDeleteEvent?: (e: PublicEvent) => boolean;
  onDeleteRequest?: (e: PublicEvent) => void;
  title?: string;
  /** ホームの下段ペイン用：外枠・見出しは親が担当 */
  embedded?: boolean;
}) {
  const sorted = useMemo(() => sortByDateTimeThenVenue(events), [events]);
  const grouped = useMemo(() => groupOthersByNormalizedTitle(sorted), [sorted]);

  if (sorted.length === 0) return null;

  return (
    <section className={embedded ? "home-day-others-section home-day-others-section--embedded" : "card home-day-others-section"} style={embedded ? undefined : { marginTop: 12 }}>
      {!embedded ? <h2 style={{ margin: "0 0 8px", fontSize: 15 }}>{title}</h2> : null}
      {grouped.map((group, gi) => (
        <div key={`other-group-${group[0]?.id ?? gi}`} className="day-other-title-group">
          {group.map((e) => (
            <OtherDetail key={e.id} e={e} onCorrectionRequest={onCorrectionRequest} canDeleteEvent={canDeleteEvent} onDeleteRequest={onDeleteRequest} />
          ))}
        </div>
      ))}
    </section>
  );
}
